//================================================================
//  Component: Diligence & Governance Tab
//================================================================

//  Purpose: This contains all the other requests related to this digital request task

//  Properties:
//    - task = {An object, contains the digital request task details}
//    - setPageStatus = {A useState, used to determine the status of the page}
//    - setTotalTasks = {A useState, used to store the total number of requests associated with this request}

//  Example:
//    <DiligenceAndGovernanceTab
//      task={task}
//      setPageStatus={setPageStatus}
//      setTotalTasks={setTotalTasks}
//    ></DiligenceAndGovernanceTab>    

// ==============================================================

// Libraries
import React, { useReducer, useState, useEffect, useContext } from 'react'

// Contexts
import { SetToast } from '../../../../Library/GlobalContexts';

// Components
import VendorRiskPane from './SidePanes/VendorRiskPane';
import AddCommercialTaskPane from './SidePanes/AddCommercialTaskPane';
import ViewCommercialTaskPane from './SidePanes/ViewCommercialTaskPane';
import AddOtherApprovalPane from './SidePanes/AddOtherApprovalPane';
import CancelApprovalModal from '../Modals/CancelApprovalModal';
import ViewOtherApproval from './SidePanes/ViewOtherApproval';
import DynamicRatingBadge from './Components/DynamicRatingBadge';
import StatusLabel from '../../../../Components/StatusLabel/StatusLabel';

// Functions
import QueryListener from '../../../../Library/QueryListener';
import ConvertDate from '../../../../Library/ConvertDate';
import WriteDocument from '../../../../Library/WriteDocument';

// Images
import Expand from '../../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../../Components/Images/Icon_Expand_Teal.svg';
import CancelDisabled from '../../../../Components/Images/Icon_CancelDisabled_Grey.svg'
import ErrorGrey from '../../../../Components/Images/Icon_Error_Grey.svg';

// CSS
import '../../Task.css'


export default function DiligenceAndGovernanceTab({
    task,
    setPageStatus,
    setTotalTasks
}) {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to store all the requests related to this demand management task
    const [allTasks, setAllTasks] = useState([]);

    // Used to toggle the visibility of the cancel other approval modal and save the current approval
    const [cancelOtherApprovalModal, setCancelOtherApprovalModal] = useState(false);

    // Used to store approval data
    const [cancelApprovalData, setCancelApprovalData] = useState(undefined);

    // Used to determine if this commercial task has any quotes in review
    const [commercialQuoteTaskStatus, setCommercialQuoteTaskStatus] = useState({});

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store whether a section is expanded and if the create pane is closed
    const [taskSectionCollapsed, setTaskSectionCollapsed] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'vendorrisk': true,
            'commercial': true,
            'otherApproval': true,
        }
    );

    // Used to determine when a pane is open and store the current task
    const [paneOpen, setPaneOpen] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'vendorrisk': false,
            'addCommercial': false,
            'viewCommercial': undefined,
            'addOtherApproval': false,
            'viewOtherApproval': undefined,
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Check if section has any requests
    function hasTask(taskType) {

        //Returns True or False
        return allTasks.filter((task) => task?.tasktype === taskType && (task?.status === 'In Progress' || task?.status === 'Not Started'))?.length > 0;

    }

    // Delete Vendor Risk Rating
    function onVendorRiskDelete(index) {

        // Removes the element at that index
        const updatedField = task.vendorriskratings.filter((_, i) => i !== index);

        // Update task document with updated vendor risk ratings list
        const updatedDoc = {
            ...task,
            vendorriskratings: updatedField
        }

        // Update the task object in the database
        WriteDocument('requests', task.requestid, updatedDoc, true)
            .catch((error) => {
                console.log(error);

                // Set error on toast
                setToast({
                'type': 'error',
                'message': 'Unable to delete vendor risk rating',
                });

                setPageStatus('error-fatal');

            });
    }

    // Cancel Other Approval
    function handleCancelApproval(data) {
        setCancelOtherApprovalModal(true);
        setCancelApprovalData(data);
    }

    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------

    // Get all the related requests
    useEffect(() => {

        if (task === undefined) return;

        function onLoadChange(document) {

            const commercialReviewTasks = document.filter(task => task.tasktype === 'Commercial Review');

            // Check total quotes in review in commercial task
            const commercialTaskStatus = commercialReviewTasks.reduce((acc, commercialTask) => {
                const hasInReview = document.some(subTask =>
                    subTask.poanumber === commercialTask.poanumber &&
                    subTask.tasktype === 'Quote Review' &&
                    subTask.status === 'For Review'
                );

                acc[commercialTask.requestid] = hasInReview;
                return acc;
            }, {});

            setCommercialQuoteTaskStatus(commercialTaskStatus);

            // Check if there is a vendor risk rating and add to total requests
            task?.vendorriskratings?.forEach(item => {
                if (item?.status === 'In Progress') {
                    document.push(item);
                }
            });

            setTotalTasks(document);
            setAllTasks(document);

        }

        function onError(error) {
            console.log(error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Unable to load requests',
            });

            setPageStatus('error-fatal');

        }

        const unsubscribe = QueryListener('requests', [
            ['parentrequestid', '==', task?.parentrequestid],
            ['tasktype', 'not-in', ['Digital Request', 'Initial Approval', 'Final Approval', 'Finance Approval', 'COO Approval', 'LoA Approval']],
        ], onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        };

        // eslint-disable-next-line
    }, [task]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col gap-2 p-0'>

            {/* ===================================================== */}
            {/*   Vendor Risk Rating (UpGuard)                        */}
            {/* ===================================================== */}

            <div className='Task-Section-Container' hidden={task?.requesttype !== 'New Initiative or Technology'}>

                <div className='flex flex-row gap-3 px-[2%] py-[5px] items-center justify-between cursor-pointer' >

                    {/* Title */}
                    <div className='flex flex-row items-center gap-2' onClick={() => setTaskSectionCollapsed({ 'vendorrisk': !taskSectionCollapsed.vendorrisk })}>
                        <img className='cursor-pointer' src={taskSectionCollapsed?.vendorrisk ? Collapse : Expand} alt={'>'}></img>
                        <h4 className='text-[20px] m-0'> Vendor Risk Rating (UpGuard) </h4>
                    </div>

                    <div className='flex flex-row gap-2 items-center'>

                        {/* Create Button */}
                        <button
                            className={task?.vendorriskratings?.length > 0 ? 'Secondary-Button' : 'Primary-Button'}
                            onClick={() => setPaneOpen({ 'vendorrisk': true })}
                            disabled={task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled'}
                        >
                            + Create
                        </button>
                    </div>

                </div>
                <hr className='m-0' hidden={taskSectionCollapsed?.vendorrisk}></hr>

                {/* ------------- VENDOR RISK RATING (UPGUARD) ------------- */}

                <div className='p-[3%] flex flex-row justify-between' hidden={taskSectionCollapsed?.vendorrisk}>

                    <div className='w-full overflow-x-auto border border-[#D8D8D8] rounded px-[1%]'>
                        <table className='w-full max-h-96 overflow-y-scroll'>
                            <thead>
                                <tr>
                                    <th className='py-[15px] px-[10px] font-medium'>Created</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Domain Name</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Created By</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Automated Rating</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Questionnaire Rating</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Overall Rating</th>
                                    <th className='py-[15px] px-[10px] font-medium'></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    task?.vendorriskratings === undefined || task?.vendorriskratings.length === 0 ?
                                        (
                                            <tr>
                                                <td className='border-t border-t-[#D8D8D8]' colSpan={8}>
                                                    <div className='text-center p-[2%]'>
                                                        Click '<strong>+</strong> Create' to add a Vendor Risk Rating
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                        :
                                        (
                                            task?.vendorriskratings?.map((item, index) => (
                                                <tr key={index} className='hover:bg-[#F0F7F7]'>

                                                    {/* Created */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {ConvertDate(item?.createddate)}
                                                    </td>

                                                    {/* Domain Name */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {item?.domainname}
                                                    </td>

                                                    {/* Created By */}
                                                    <td className='py-[10px] pl-[10px] pr-[0px] border-t border-t-[#D8D8D8]'>
                                                        <div className='flex flex-col'>
                                                            {item?.createdbygivenname} {item?.createdbysurname}
                                                            <label className='text-[14px] text-[#616161]'>{item?.createdbyemail}</label>
                                                        </div>
                                                    </td>

                                                    {/* Automated Rating */}
                                                    {
                                                        <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] mb-0 align-middle'>
                                                            {
                                                                // Status - In Progress
                                                                item?.status === 'In Progress' ?
                                                                    <StatusLabel status={'In Progress'}></StatusLabel>

                                                                    // Status - Failed
                                                                    : item?.status === 'Failed' ?
                                                                        <div className='flex flex-row items-center gap-2'>
                                                                            <img src={ErrorGrey} width={'26px'} height={'26px'} alt='Failed'></img>
                                                                            {item.errormessage ? <span className='text-sm'>{item.errormessage}</span> : 'Failed'}
                                                                        </div>

                                                                        // Else > Status - Success
                                                                        :
                                                                        <div className='flex flex-row items-center gap-2'>
                                                                            {
                                                                                item?.automatedrating?.grade === '' ||  item?.automatedrating?.score === '' ?
                                                                                    <span> N/A </span>
                                                                                    :
                                                                                    <>
                                                                                        <DynamicRatingBadge letter={item?.automatedrating?.grade}></DynamicRatingBadge>
                                                                                        <span>{item?.automatedrating?.score}</span>
                                                                                    </>
                                                                            }
                                                                        </div>
                                                            }
                                                        </td>
                                                    }

                                                    {/* Questionnaire Rating */}
                                                    {
                                                        <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] mb-0 align-middle'>
                                                            {
                                                                // Status - In Progress
                                                                item?.status === 'In Progress' ?
                                                                    <StatusLabel status={'In Progress'}></StatusLabel>

                                                                    // Status - Failed
                                                                    : item?.status === 'Failed' ?
                                                                        <div className='flex flex-row items-center gap-2'>
                                                                            <img src={ErrorGrey} width={'26px'} height={'26px'} alt='Failed'></img>
                                                                            {item.errormessage ? <span className='text-sm'>{item.errormessage}</span> : 'Failed'}
                                                                        </div>

                                                                        // Else > Status - Success
                                                                        :
                                                                        <div className='flex flex-row items-center gap-2'>
                                                                            {
                                                                                item?.questionnairerating?.grade === '' ||  item?.questionnairerating?.score === '' ?
                                                                                    <span> N/A </span>
                                                                                    :
                                                                                    <>
                                                                                        <DynamicRatingBadge letter={item?.questionnairerating?.grade}></DynamicRatingBadge>
                                                                                        <span>{item?.questionnairerating?.score}</span>
                                                                                    </>
                                                                            }
                                                                        </div>
                                                            }
                                                        </td>
                                                    }

                                                    {/* Overall Rating */}
                                                    {
                                                        <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] mb-0 align-middle'>
                                                            {
                                                                // Status - In Progress
                                                                item?.status === 'In Progress' ?
                                                                    <StatusLabel status={'In Progress'}></StatusLabel>

                                                                    // Status - Failed
                                                                    : item?.status === 'Failed' ?
                                                                        <div className='flex flex-row items-center gap-2'>
                                                                            <img src={ErrorGrey} width={'26px'} height={'26px'} alt='Failed'></img>
                                                                            {item.errormessage ? <span className='text-sm'>{item.errormessage}</span> : 'Failed'}
                                                                        </div>

                                                                        // Else > Status - Success
                                                                        :
                                                                        <div className='flex flex-row items-center gap-2'>
                                                                            {
                                                                                item?.overallrating?.grade === '' || item?.overallrating?.score === '' ?
                                                                                    <span> N/A </span>
                                                                                    :
                                                                                    <>
                                                                                        <DynamicRatingBadge letter={item?.overallrating?.grade}></DynamicRatingBadge>
                                                                                        <span>{item?.overallrating?.score}</span>
                                                                                    </>
                                                                            }
                                                                        </div>
                                                            }
                                                        </td>
                                                    }

                                                    {/* Cancel */}
                                                    <td className='m-4 border-t border-t-[#D8D8D8]'>
                                                        {
                                                            item?.status === 'In Progress' ?
                                                                <img src={CancelDisabled} alt='Cancel' className='cursor-not-allowed min-w-[24px]'></img>
                                                                :
                                                                <div className='Cancel-Icon' onClick={() => onVendorRiskDelete(index)}></div>
                                                        }
                                                    </td>

                                                </tr>
                                            ))
                                        )
                                }
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*   Commercial & Procurement                            */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[5px] items-center justify-between cursor-pointer' >

                    <div className='flex flex-row items-center gap-2' onClick={() => setTaskSectionCollapsed({ 'commercial': !taskSectionCollapsed.commercial })}>
                        <img
                            className='cursor-pointer'
                            src={taskSectionCollapsed?.commercial ? Collapse : Expand}
                            alt={'>'}
                        ></img>

                        <h4 className='text-[20px] m-0'> Commercial & Procurement </h4>
                    </div>

                    <div className='flex flex-row gap-2 items-center'>

                        {/* Quote Ready For Review Pill */}
                        <div
                            className='flex items-center justify-center px-3 py-2 font-semibold rounded-full text-sm text-[#00A7A4] border-[1.5px] border-[#00A7A4] bg-[#F2FFFF]'
                            hidden={
                                !Object.values(commercialQuoteTaskStatus).some(val => val === true)
                            }
                        >
                            Quote Ready For Review
                        </div>

                        {/* Total Tasks */}
                        <div className='flex w-[30px] h-[30px] items-center justify-center px-2 py-0 rounded-md font-medium text-[#00A7A4] border-[1.5px] border-[#00A7A4] bg-[#F2FFFF]' hidden={!hasTask('Commercial Review')}>
                            {allTasks.filter((task) => task?.tasktype === 'Commercial Review' && (task?.status === 'In Progress' || task?.status === 'Not Started'))?.length}
                        </div>

                        {/* Create Button */}
                        <button
                            className={hasTask('Commercial Review') ? 'Secondary-Button' : 'Primary-Button'}
                            onClick={() => setPaneOpen({ 'addCommercial': true })}
                            disabled={task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled'}
                        >
                            + Create
                        </button>
                    </div>

                </div>
                <hr className='m-0' hidden={taskSectionCollapsed?.commercial}></hr>

                {/* ------------- COMMERCIAL ------------- */}

                <div className='p-[3%] flex flex-row justify-between' hidden={taskSectionCollapsed?.commercial}>

                    <div className='w-full overflow-x-auto border border-[#D8D8D8] rounded px-[1%]'>
                        <table className='w-full max-h-96 overflow-y-scroll'>
                            <thead>
                                <tr>
                                    <th className='py-[15px] px-[5px] font-medium'></th>
                                    <th className='py-[15px] px-[10px] font-medium'>PoA Number</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Created</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Task Name</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Modified</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Assigned To</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Status</th>
                                    <th className='py-[15px] px-[10px] font-medium'></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    allTasks.filter((task) => task?.tasktype === 'Commercial Review')?.length === 0 || allTasks.filter((task) => task?.tasktype === 'Commercial Review') === undefined ?
                                        (
                                            <tr>
                                                <td className='border-t border-t-[#D8D8D8]' colSpan={8}>
                                                    <div className='text-center p-[2%]'>
                                                        Click '<strong>+</strong> Create' to add a task
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                        :
                                        (
                                            allTasks.filter((item) => item?.tasktype === 'Commercial Review')?.map((item, index) => (
                                                <tr key={index} className='hover:bg-[#F0F7F7] cursor-pointer' onClick={() => setPaneOpen({ 'viewCommercial': item })}>

                                                    <td className='py-[10px] px-[5px] border-t border-t-[#D8D8D8]'>
                                                        {
                                                            commercialQuoteTaskStatus[item?.requestid] &&
                                                            <div className='w-2 h-2 bg-[#C4314B] rounded-full'></div>
                                                        }
                                                    </td>

                                                    {/* PoA Number */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {item?.poanumber}
                                                    </td>

                                                    {/* Created */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {ConvertDate(item?.createddate)}
                                                    </td>

                                                    {/* Task Name */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {item?.taskname}
                                                    </td>

                                                    {/* Modified Date */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {ConvertDate(item?.lastmodifieddate)}
                                                    </td>

                                                    {/* Assigned To */}
                                                    <td className='py-[10px] pl-[10px] pr-[0px] border-t border-t-[#D8D8D8]'>
                                                        {
                                                            item?.assignedto?.email === 'Unassigned' ?
                                                                <label>Unassigned</label>
                                                                :
                                                                <div className='flex flex-col'>
                                                                    <label className='capitalize'>{item?.assignedto?.givenname} {item?.assignedto?.surname} </label>
                                                                    <label className='text-[14px] text-[#616161]'>{item?.assignedto?.email}</label>
                                                                </div>
                                                        }
                                                    </td>

                                                    {/* Status */}
                                                    <td className='pt-[15px] pb-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <StatusLabel status={item?.status} tasktype={task?.tasktype}></StatusLabel>
                                                    </td>

                                                    {/* Cancel */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'></td>
                                                </tr>
                                            ))
                                        )
                                }
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*   Other Approval                                      */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[5px] items-center justify-between cursor-pointer'>

                    <div className='flex flex-row items-center gap-2' onClick={() => setTaskSectionCollapsed({ 'otherApproval': !taskSectionCollapsed.otherApproval })}>
                        <img className='cursor-pointer' src={taskSectionCollapsed?.otherApproval ? Collapse : Expand} alt={'>'}></img>
                        <h4 className='text-[20px] m-0'> Other Approval </h4>
                    </div>

                    <div className='flex flex-row gap-2 items-center'>

                        {/* Total Tasks */}
                        <div className='flex w-[30px] h-[30px] items-center justify-center px-2 py-0 rounded-md font-medium text-[#00A7A4] border-1 border-[#00A7A4] bg-[#F2FFFF]' hidden={!hasTask('Other Approval')}>
                            {allTasks.filter((task) => task?.tasktype === 'Other Approval')?.length}
                        </div>

                        {/* Create Button */}
                        <button
                            className={hasTask('Other Approval') ? 'Secondary-Button' : 'Primary-Button'}
                            onClick={() => setPaneOpen({ 'addOtherApproval': true })}
                            disabled={task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled'}
                        >
                            + Create
                        </button>

                    </div>

                </div>
                <hr className='m-0' hidden={taskSectionCollapsed?.otherApproval}></hr>

                {/* ------------- OTHER APPROVAL ------------- */}

                <div className='p-[3%] flex flex-row justify-between' hidden={taskSectionCollapsed?.otherApproval}>

                    <div className='w-full overflow-x-auto border border-[#D8D8D8] rounded px-[1%]'>
                        <table className='w-full max-h-96 overflow-y-scroll'>
                            <thead>
                                <tr>
                                    <th className='py-[15px] px-[5px] font-medium'></th>
                                    <th className='py-[15px] px-[10px] font-medium'>Approver</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Subject</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Modified</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Status</th>
                                    <th className='py-[15px] px-[10px] font-medium'></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    allTasks.filter((task) => task?.tasktype === 'Other Approval')?.length === 0 || allTasks.filter((task) => task?.tasktype === 'Other Approval') === undefined ?
                                        (
                                            <tr>
                                                <td className='border-t border-t-[#D8D8D8]' colSpan={6}>
                                                    <div className='text-center p-[2%]'>
                                                        Click '<strong>+</strong> Create' to add a task
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                        :
                                        (
                                            allTasks.filter((task) => task?.tasktype === 'Other Approval')?.map((item, index) => (
                                                <tr key={index} className='hover:bg-[#F0F7F7] cursor-pointer'>

                                                    <td className='py-[10px] px-[5px] border-t border-t-[#D8D8D8]'></td>

                                                    {/* Approver */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] cursor-pointer'>
                                                        {
                                                            item?.assignedto?.email === 'Unassigned' ?
                                                                <label>Unassigned</label>
                                                                :
                                                                <div className='flex flex-col'>
                                                                    <label className='capitalize cursor-pointer'>{item?.assignedto?.givenname} {item?.assignedto?.surname} </label>
                                                                    <label className='text-[14px] text-[#616161] cursor-pointer'>{item?.assignedto?.email}</label>
                                                                </div>
                                                        }
                                                    </td>

                                                    {/* Subject */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] hover:underline' onClick={() => { setPaneOpen({ 'viewOtherApproval': item }) }}>
                                                        {item?.taskname}
                                                    </td>

                                                    {/* Modified Date */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {ConvertDate(item?.lastmodifieddate)}
                                                    </td>

                                                    {/* Status */}
                                                    <td className='pt-[15px] pb-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <div>
                                                            {
                                                                [
                                                                    { 'name': 'Not Started', 'background': '#FBF6D9', 'color': '#DC9A03', 'bordercolor': '#DC9A03', 'status': 'Not Started' },
                                                                    { 'name': 'In Progress', 'background': '#FBF6D9', 'color': '#DC9A03', 'bordercolor': '#DC9A03', 'status': 'In Progress' },
                                                                    { 'name': 'Approved', 'background': '#E7F2DA', 'color': '#237B4B', 'bordercolor': '#237B4B', 'status': 'Completed' },
                                                                    { 'name': 'Cancelled', 'background': '#EBEBEB', 'color': '#616161', 'bordercolor': '#616161', 'status': 'Cancelled' },
                                                                    { 'name': 'Rejected', 'background': '#FCF4F6', 'color': '#C4314B', 'bordercolor': '#C4314B', 'status': 'Rejected' },

                                                                ].filter((style) => style?.status === item?.status).map((object, index) => (
                                                                    <label key={index} className='Status-Label' style={{ backgroundColor: `${object.background}`, color: `${object.color}`, border: `1px solid ${object.bordercolor}` }} >
                                                                        {object.name}
                                                                    </label>
                                                                ))
                                                            }
                                                        </div>
                                                    </td>

                                                    {/* Cancel */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {
                                                            item?.status !== 'In Progress' ?
                                                                <img src={CancelDisabled} alt='Cancel' className='cursor-not-allowed min-w-[24px]'></img>
                                                                :
                                                                <div className='Cancel-Icon' onClick={() => handleCancelApproval(item)}></div>
                                                        }
                                                    </td>

                                                </tr>
                                            ))
                                        )
                                }
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*   Side Panes                                          */}
            {/* ===================================================== */}

            {/* Vendor Risk Rating */}
            <VendorRiskPane
                paneOpen={paneOpen}
                setPaneOpen={setPaneOpen}
                task={task}
            ></VendorRiskPane>

            {/* Commercial Task */}
            <AddCommercialTaskPane
                paneOpen={paneOpen}
                setPaneOpen={setPaneOpen}
                task={task}
            ></AddCommercialTaskPane>

            <ViewCommercialTaskPane
                paneOpen={paneOpen}
                setPaneOpen={setPaneOpen}
            ></ViewCommercialTaskPane>

            {/* Other Approval */}
            <AddOtherApprovalPane
                paneOpen={paneOpen}
                setPaneOpen={setPaneOpen}
                task={task}
            ></AddOtherApprovalPane>

            <ViewOtherApproval
                paneOpen={paneOpen}
                setPaneOpen={setPaneOpen}
            ></ViewOtherApproval>

            {/* ===================================================== */}
            {/*  Modals                                               */}
            {/* ===================================================== */}

            <CancelApprovalModal
                cancelRequestModal={cancelOtherApprovalModal}
                setCancelRequestModal={setCancelOtherApprovalModal}
                task={cancelApprovalData}
            ></CancelApprovalModal>


        </div>
    )
}
